/**
 * Wire contract for the fits-for-models JSON ISLAND — the client-side mirror
 * of the plugin-assembled payload (spare_parts_platform
 * `CompatContentProvider::provideFinderPayloadJson` →
 * `FitsForPayloadAssembler`; PHP-side validation: `FitsForPayloadGuard`).
 *
 * The island is plain JSON emitted by the template layer at render time
 * (the `{ersatzteil_finder}` seam) — it never travels through JTL text
 * fields, so the ws5_autolinker never sees it and no base64 carrier exists
 * anymore. `zod.parse` is the runtime trust boundary: anything unparseable
 * ⇒ the finder does not boot. Kept honest against the PHP assembler by the
 * shared island fixture (tests/fixtures/fits-for-models.island.example.json,
 * generated by FitsForPayloadAssemblerTest).
 *
 * @see docs/plans/2026-07-09 - interactive-shop-foundation/adrs/adr-finder-json-island.md
 */

import { z } from 'zod';

/** A part marker: position label + CENTER coordinates as fractions [0,1] of the drawing frame. */
export const DrawingMarkerSchema = z.object({
  label: z.string().min(1),
  x: z.number().finite(),
  y: z.number().finite(),
});
export type DrawingMarker = z.infer<typeof DrawingMarkerSchema>;

/**
 * One drawing under a spectrum. Two flavours share the shape:
 *  - an IPL drawing (partlist leaf, the finder's "Variante" axis) — `imageUrl`
 *    is the exploded diagram, `markers` pin the part on it;
 *  - a "device image" for a fits-for model with NO drawable IPL (`isDeviceImage`
 *    true) — `imageUrl` is the model's category thumbnail (or `null` when even
 *    that is unavailable), `markers` is always empty. This is what merges the
 *    former standalone "Passt für diese Geräte" list into the finder: every
 *    compatible model appears, drawn where it can be, listed where it cannot.
 */
export const DrawingSchema = z.object({
  /** Panel DOM id (shared PanelIdPolicy) — the integer markup token */
  foreignId: z.string().min(1),
  /**
   * The `category` URL value when it differs from `foreignId`: the public id
   * (`datte-ipl-<publicId>`, `datte-node-<publicId>-device`). Absent = the URL uses
   * `foreignId` (legacy anchors, rows without a public id).
   */
  urlId: z.string().regex(/^[A-Za-z0-9_-]+$/).optional(),
  name: z.string().min(1),
  /** Category-page path (site-relative, no leading slash) */
  categoryUrl: z.string().min(1),
  /** Diagram/thumbnail URL; `null` for a fits-for model with no resolvable image */
  imageUrl: z.string().min(1).nullable(),
  markers: z.array(DrawingMarkerSchema),
  /** true ⇒ a fits-for device thumbnail (no IPL, no pins), styled as a photo not a diagram */
  isDeviceImage: z.boolean().optional(),
});
export type Drawing = z.infer<typeof DrawingSchema>;

/**
 * One model_year/Spektrum node under a model ('Standard' synthetic when
 * absent). The "Baujahr" axis shows `label` (normalized for display in the
 * view) — no structured year parse (D4 revision 2026-07-07).
 */
export const SpectrumSchema = z.object({
  /** Variant markup token (`datte-node-<id>`); '' for the synthetic 'Standard' spectrum */
  foreignId: z.string(),
  /** The `spectrum` URL value (`datte-node-<publicId>`); absent = the URL uses `foreignId` */
  urlId: z.string().regex(/^[A-Za-z0-9_-]+$/).optional(),
  label: z.string().min(1),
  drawings: z.array(DrawingSchema).min(1),
});
export type Spectrum = z.infer<typeof SpectrumSchema>;

/** One device model the part fits. */
export const CompatibleModelSchema = z.object({
  device: z.string().min(1),
  name: z.string().min(1),
  spectrums: z.array(SpectrumSchema).min(1),
});
export type CompatibleModel = z.infer<typeof CompatibleModelSchema>;

export const ManufacturerGroupSchema = z.object({
  name: z.string().min(1),
  models: z.array(CompatibleModelSchema).min(1),
});
export type ManufacturerGroup = z.infer<typeof ManufacturerGroupSchema>;

export const FitsForModelsPayloadSchema = z.object({
  kind: z.literal('fits-for-models'),
  /** The product's HAN — the `article` param on category-page CTAs */
  han: z.string().min(1),
  manufacturers: z.array(ManufacturerGroupSchema).min(1),
});
export type FitsForModelsPayload = z.infer<typeof FitsForModelsPayloadSchema>;

export const FitsForModelsEnvelopeSchema = z.object({
  version: z.literal(1),
  value: FitsForModelsPayloadSchema,
});
export type FitsForModelsEnvelope = z.infer<typeof FitsForModelsEnvelopeSchema>;

/**
 * Parse the island body (plain envelope JSON). `null` ⇒ not a v1 payload —
 * the caller leaves the container alone. Unknown versions are logged loudly
 * (ADR failure modes) so a deploy-order violation is visible, not silent.
 */
export function parseFitsForPayload(raw: string): FitsForModelsPayload | null {
  let data: unknown;
  try {
    data = JSON.parse(raw.trim());
  } catch {
    return null;
  }
  const result = FitsForModelsEnvelopeSchema.safeParse(data);
  if (!result.success) {
    const version = (data as { version?: unknown } | null)?.version;
    if (typeof version === 'number' && version !== 1) {
      console.error(
        `[ersatzteil-finder] unsupported fits-for-models payload version ${version}: finder not booted`
      );
    }
    return null;
  }
  return result.data.value;
}
